/**
 * Who sees an itinerary tile, and when.
 *
 * The database has carried `status` and `audience` since the timeline
 * foundation, both CHECK-constrained, and the guest read has always filtered on
 * them:
 *
 *   public page        status IN ('published','cancelled') AND audience = 'all'
 *   invite-token page  status IN ('published','cancelled') AND audience IN ('all','invitees')
 *
 * Studio never sent either field, so every tile was born `published` + `all`.
 * Pressing Save put it in front of every guest on the trip — a placeholder, a
 * mistyped time, a venue nobody had confirmed. There was no way to write
 * something down without publishing it, because the switch existed and the
 * screen could not reach it.
 *
 * This module is the vocabulary for that switch. It lives apart from the tab so
 * the reach of a tile is a fact with tests rather than a conditional inside a
 * render.
 */
import type { GroupItineraryItem } from '../types/groups';

/** `cancelled` is set only by the audited cancel transition, never by the upsert. */
export type ItineraryStatus = 'draft' | 'published' | 'cancelled';
export type ItineraryAudience = 'all' | 'invitees' | 'private';

/** The two a host may choose. Cancelling has its own endpoint and its own reason. */
export const HOST_SETTABLE_STATUSES: readonly ItineraryStatus[] = ['draft', 'published'];
export const ITINERARY_AUDIENCES: readonly ItineraryAudience[] = ['all', 'invitees', 'private'];

export const ITINERARY_STATUS_LABELS: Readonly<Record<ItineraryStatus, string>> = {
  draft: 'Draft',
  published: 'Published',
  cancelled: 'Cancelled',
};

export const ITINERARY_AUDIENCE_LABELS: Readonly<Record<ItineraryAudience, string>> = {
  all: 'Everyone on the trip',
  invitees: 'Invited guests only',
  private: 'Just the hosts',
};

export const ITINERARY_AUDIENCE_HINTS: Readonly<Record<ItineraryAudience, string>> = {
  all: 'The plan itself — dinners, the boat, where to be at what time.',
  invitees: 'On the invite link only, never the public page.',
  private: 'Run of show: call times, vendor arrivals, when the cake comes out.',
};

export function itineraryStatus(item: Pick<GroupItineraryItem, 'status'>): ItineraryStatus {
  const raw = String(item.status ?? '').trim().toLowerCase();
  return raw === 'draft' || raw === 'cancelled' ? raw : 'published';
}

export function itineraryAudience(item: Pick<GroupItineraryItem, 'audience'>): ItineraryAudience {
  const raw = String(item.audience ?? '').trim().toLowerCase();
  return raw === 'invitees' || raw === 'private' ? raw : 'all';
}

/** Who a tile actually reaches, mirroring the two guest reads above. */
export type ItineraryReach = 'nobody' | 'invited' | 'everyone';

export function itineraryReach(status: ItineraryStatus, audience: ItineraryAudience): ItineraryReach {
  // A draft is invisible whatever its audience says — status is the outer gate,
  // which is what lets a host sketch Saturday with the audience already set.
  if (status === 'draft') return 'nobody';
  if (audience === 'private') return 'nobody';
  return audience === 'all' ? 'everyone' : 'invited';
}

/**
 * The sentence under the controls, in the tense of what Save will do.
 *
 * `inviteOnly` matters: on an invite-only trip the public page refuses anyone
 * without a token, so "Everyone" and "Invited guests only" reach the same
 * people and saying otherwise sells a protection that is not there.
 */
export function visibilityNote(
  status: ItineraryStatus,
  audience: ItineraryAudience,
  inviteOnly: boolean,
): string {
  if (status === 'cancelled') {
    return audience === 'private'
      ? 'Cancelled, and hosts-only — no guest ever saw this one.'
      : 'Cancelled. Guests see this in place of the plan, with your reason.';
  }
  if (status === 'draft') {
    return 'Draft — nobody but the hosts can see this. Publish it when it is real.';
  }
  if (audience === 'private') {
    return 'Hosts only. This never reaches the guest page, published or not.';
  }
  if (audience === 'invitees') {
    return inviteOnly
      ? 'Invited guests. This trip is invite-only, so that is everyone who can open the page at all.'
      : 'Guests who open their own invite link. Not on the public page.';
  }
  return inviteOnly
    ? 'Every guest on this trip, as soon as you save.'
    : 'Every guest, and anyone with the public link, as soon as you save.';
}

/**
 * The badge a card carries, or null.
 *
 * Published-to-everyone is the norm and gets no badge — a badge on every card
 * is a badge on none. The two that need saying are the ones where the host's
 * mental model and the guest page disagree.
 */
export function visibilityBadge(
  item: Pick<GroupItineraryItem, 'status' | 'audience'>,
): { label: string; tone: 'default' | 'warning' | 'info' } | null {
  const status = itineraryStatus(item);
  const audience = itineraryAudience(item);
  if (status === 'draft') return { label: 'Draft · not visible to guests', tone: 'warning' };
  if (status === 'cancelled') return null; // the card already carries the cancellation notice
  if (audience === 'private') return { label: 'Hosts only', tone: 'default' };
  if (audience === 'invitees') return { label: 'Invited guests only', tone: 'info' };
  return null;
}

/**
 * Whether "Cancel event" belongs on this card.
 *
 * Not on a draft, and this is the one place the draft lane can bite. The guest
 * read admits `status IN ('published','cancelled')` — so cancelling a tile that
 * was never published PUBLISHES it, as a cancelled event, to guests who never
 * saw the plan it cancels. Cancellation is an apology for a change of plan; a
 * draft has no plan to change.
 *
 * There is no delete endpoint, so a draft the host has finished with simply
 * stays a draft. That is invisible to guests and costs nothing but a card.
 */
export function canCancel(status: ItineraryStatus): boolean {
  return status === 'published';
}

/**
 * The two fields to send with a save.
 *
 * Both explicit, so the screen can actually say something about visibility —
 * the API reads null as "preserve" on an update and defaults it on an insert,
 * which is how Studio ended up unable to express either.
 *
 * The one exception is a cancelled item. The upsert refuses to WRITE
 * `cancelled` (it has nowhere to put the reason), so sending the status back
 * would 400; sending `published` instead would quietly resurrect a cancelled
 * event on guests' timelines. Omitting it preserves what is stored, which is
 * the only correct answer.
 */
export function visibilityFields(
  status: ItineraryStatus,
  audience: ItineraryAudience,
): { status?: ItineraryStatus; audience: ItineraryAudience } {
  return status === 'cancelled' ? { audience } : { status, audience };
}
